import RespImage from '@/app/_components/core/resp-image';
import {
  PARTNER_IMAGE_CLASSES,
  PARTNER_IMAGE_SIZES,
  type LogoSize,
} from '@/app/_components/shared/partner-list/utils/logo-size';
import { transformSrcsetCandidates } from '@/app/_lib/image-art-direction';
import { emptyToUndefined, isSvgUrl } from '@/app/_lib/utils';
import type { PartnerItem } from '@/entities/models/page';

type PartnerImageProps = Pick<
  PartnerItem,
  'imageUrl' | 'imageWidth' | 'imageHeight' | 'srcsetCandidates' | 'altText' | 'title' | 'text'
> & {
  logoSize?: LogoSize;
};

export const PartnerImage = ({
  imageUrl,
  imageWidth,
  imageHeight,
  srcsetCandidates: rawSrcsetCandidates,
  altText,
  title,
  text,
  logoSize = 'small',
}: PartnerImageProps) => {
  const { srcsetCandidates, artDirection } = rawSrcsetCandidates
    ? transformSrcsetCandidates(rawSrcsetCandidates)
    : { srcsetCandidates: [], artDirection: undefined };

  const hasSrcsetImage = srcsetCandidates.length > 0;
  const alt = emptyToUndefined(altText) ?? emptyToUndefined(text) ?? '';
  const imageClasses = PARTNER_IMAGE_CLASSES[logoSize];
  const imageSizes = PARTNER_IMAGE_SIZES[logoSize];

  // SVGs should be rendered as plain img tags (no optimization needed for vectors)
  if (imageUrl && isSvgUrl(imageUrl)) {
    return (
      // eslint-disable-next-line @next/next/no-img-element -- SVGs don't benefit from Next Image optimization
      <img
        src={imageUrl}
        alt={alt}
        width="144"
        height="20"
        title={emptyToUndefined(title)}
        className={imageClasses}
        loading="lazy"
      />
    );
  }

  // Raster images with srcset candidates use RespImage for responsive loading
  if (hasSrcsetImage) {
    return (
      <RespImage
        srcsetCandidates={srcsetCandidates}
        artDirection={artDirection}
        alt={alt}
        title={title}
        sizes={imageSizes}
        className={imageClasses}
        loading="lazy"
      />
    );
  }

  // Fallback for direct image URL without srcset (non-SVG raster images)
  if (imageUrl) {
    return (
      // eslint-disable-next-line @next/next/no-img-element -- External URLs without srcset, RespImage handles optimized cases
      <img
        src={imageUrl}
        alt={alt}
        width={imageWidth ?? 144}
        height={imageHeight ?? 20}
        title={emptyToUndefined(title)}
        className={imageClasses}
        loading="lazy"
      />
    );
  }

  return null;
};
